/**
 * WP4f / #46612 — verification-onboarding contracts for Real first-run
 * empty states and the first receipt path (HG5).
 *
 * Session-authenticated product UI API (`apiCall` / `useApi`). Distinct from
 * the token-scoped public catalog at `/api/verification/v1`.
 *
 * Environment: every call carries `env` (query) and/or
 * `x-excellent-verification-environment`. Object ids are namespaced
 * `demo:` / `real:` (WP2). Handlers must call `enforceVerificationEnvironment`.
 *
 * Empty Real overview next actions are define-a-workflow or connect-a-source.
 * Workflow intake is paste/manual first; external connectors are optional.
 * Local model absence fails closed with a repair path and no frontier fallback.
 * The first successful Real run issues a receipt reachable from Overview,
 * Runs, Receipts, and Search.
 *
 * HG11: first-loop mutation uses existing `api:write` (admin/owner). No new
 * capabilities. Reads are ungated at the Hono seam, same as WP4a overview.
 *
 * WP4a–WP4e contract files are out of scope — do not edit them. Search server
 * (`excellent/server/routes/search.ts`) is out of scope (#46618). Schema is
 * WP4g. Register `test-verification-real-first-loop` in WP12.
 */

import { z } from "zod";
import type { Operation } from "../types";

export const VERIFICATION_ONBOARDING_SCHEMA_VERSION = 1 as const;

const envName = z.enum(["demo", "real"]);
export const verificationOnboardingEnvQuery = z.object({
  env: envName.optional(),
  actorId: z.string().min(1).max(200).optional(),
});
const namespacedId = z.string().min(1).max(200);
const iso = z.string().min(1);

const provenance = z.object({
  environment: envName,
  demoScoped: z.boolean(),
  trustChain: envName,
});

export type VerificationOnboardingEnvQuery = z.infer<typeof verificationOnboardingEnvQuery>;
export type VerificationOnboardingProvenance = z.infer<typeof provenance>;

export const verificationOnboardingNextActionDto = z.object({
  id: z.enum(["define-workflow", "connect-source"]),
  href: z.string().min(1),
  label: z.string().min(1),
  detail: z.string().min(1),
  primary: z.boolean(),
});
export type VerificationOnboardingNextActionDto = z.infer<
  typeof verificationOnboardingNextActionDto
>;

export const verificationOnboardingLocalModelDto = z.object({
  available: z.boolean(),
  reason: z.enum([
    "ok",
    "local-model-absent",
    "frontier-forbidden",
    "local-model-unparseable",
    "semantic-host-unavailable",
  ]),
  message: z.string().min(1),
  fixCommand: z.string().min(1).nullable(),
  frontierFallback: z.literal(false),
});
export type VerificationOnboardingLocalModelDto = z.infer<
  typeof verificationOnboardingLocalModelDto
>;

export const verificationOnboardingSourceSetupDto = z.object({
  provider: z.string().min(1),
  displayName: z.string().min(1),
  requiredScopes: z.array(z.string().min(1)),
  custody: z.string().min(1),
  health: z.enum(["not-connected", "healthy", "degraded", "error"]),
});
export type VerificationOnboardingSourceSetupDto = z.infer<
  typeof verificationOnboardingSourceSetupDto
>;

export const verificationOnboardingReceiptReachabilityDto = z.object({
  receiptId: namespacedId,
  runId: namespacedId,
  overviewHref: z.string().min(1),
  runsHref: z.string().min(1),
  receiptsHref: z.string().min(1),
  searchQuery: z.string().min(1),
});
export type VerificationOnboardingReceiptReachabilityDto = z.infer<
  typeof verificationOnboardingReceiptReachabilityDto
>;

export const verificationOnboardingLoopDto = z.object({
  workflowId: namespacedId,
  evidenceId: namespacedId,
  runId: namespacedId,
  findingId: namespacedId,
  receiptId: namespacedId,
  completedAt: iso,
});
export type VerificationOnboardingLoopDto = z.infer<typeof verificationOnboardingLoopDto>;

export const verificationOnboardingGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_ONBOARDING_SCHEMA_VERSION),
  provenance,
  empty: z.boolean(),
  nextActions: z.array(verificationOnboardingNextActionDto).min(1).max(2),
  intake: z.object({
    mode: z.literal("paste-manual"),
    connectorsOptional: z.literal(true),
  }),
  localModel: verificationOnboardingLocalModelDto,
  sources: z.array(verificationOnboardingSourceSetupDto),
  firstReceipt: verificationOnboardingReceiptReachabilityDto.nullable(),
  loop: verificationOnboardingLoopDto.nullable(),
});
export type VerificationOnboardingGetResponse = z.infer<typeof verificationOnboardingGetResponse>;

export const verificationOnboardingFirstLoopBody = z.object({
  actorId: namespacedId,
  name: z.string().min(1),
  doc: z.string(),
  oracle: z.string().min(1).optional(),
  evidence: z.string().min(1).optional(),
  /** Rejected unless local. Frontier providers fail closed with no fallback. */
  modelProvider: z.string().min(1).optional(),
});
export type VerificationOnboardingFirstLoopBody = z.infer<
  typeof verificationOnboardingFirstLoopBody
>;

export const verificationOnboardingFirstLoopResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_ONBOARDING_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  workflowId: namespacedId,
  evidenceId: namespacedId,
  runId: namespacedId,
  findingId: namespacedId,
  receiptId: namespacedId,
  reachability: verificationOnboardingReceiptReachabilityDto,
});
export type VerificationOnboardingFirstLoopResponse = z.infer<
  typeof verificationOnboardingFirstLoopResponse
>;

export const verificationOnboardingLocalModelBody = z.object({
  actorId: namespacedId,
  available: z.boolean(),
});
export type VerificationOnboardingLocalModelBody = z.infer<
  typeof verificationOnboardingLocalModelBody
>;

export const verificationOnboardingLocalModelResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_ONBOARDING_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  localModel: verificationOnboardingLocalModelDto,
});
export type VerificationOnboardingLocalModelResponse = z.infer<
  typeof verificationOnboardingLocalModelResponse
>;

const onboardingGetOp: Operation<
  void,
  VerificationOnboardingEnvQuery,
  void,
  VerificationOnboardingGetResponse
> = {
  method: "GET",
  path: () => "/api/verification/onboarding",
  query: verificationOnboardingEnvQuery,
  response: verificationOnboardingGetResponse,
};

const firstLoopOp: Operation<
  void,
  VerificationOnboardingEnvQuery,
  VerificationOnboardingFirstLoopBody,
  VerificationOnboardingFirstLoopResponse
> = {
  method: "POST",
  path: () => "/api/verification/onboarding/first-loop",
  query: verificationOnboardingEnvQuery,
  body: verificationOnboardingFirstLoopBody,
  response: verificationOnboardingFirstLoopResponse,
  invalidates: [onboardingGetOp],
};

const localModelOp: Operation<
  void,
  VerificationOnboardingEnvQuery,
  VerificationOnboardingLocalModelBody,
  VerificationOnboardingLocalModelResponse
> = {
  method: "POST",
  path: () => "/api/verification/onboarding/local-model",
  query: verificationOnboardingEnvQuery,
  body: verificationOnboardingLocalModelBody,
  response: verificationOnboardingLocalModelResponse,
  invalidates: [onboardingGetOp],
};

export const verificationOnboarding = {
  get: onboardingGetOp,
  firstLoop: firstLoopOp,
  localModel: localModelOp,
};
